import { pushEvent } from './gtm'

/**
 * Generic click-tracking layer for marketing self-serve in GTM.
 *
 * Any element tagged with `data-el="<id>"` (optionally grouped by a
 * `data-el-section="<name>"` ancestor) emits a single `ui_click` dataLayer
 * event when clicked. Marketing configures ONE Custom Event trigger (`ui_click`)
 * in GTM and filters on the pushed variables — no code change per campaign.
 *
 * The DOM attribute is deliberately neutral (`data-el`, not `data-track`): it
 * ships in exposed HTML, and a tracking-flavored name both leaks intent and is
 * fingerprinted by content blockers. GTM only ever reads the dataLayer, never
 * the attribute name, so the pushed keys stay descriptive.
 */

const ID_ATTR = 'data-el'
const SECTION_ATTR = 'data-el-section'
const MAX_TEXT_LEN = 100

let initialized = false

/** Attach the document-level listener once. No-ops outside the browser. */
export function initClickTracking(): void {
  if (initialized || typeof document === 'undefined') return
  initialized = true
  // Capture phase: fires before React's synthetic handlers and survives
  // downstream stopPropagation. We only read — never intercept — the click.
  document.addEventListener('click', handleClick, { capture: true })
}

function handleClick(event: MouseEvent): void {
  const target = event.target
  if (!(target instanceof Element)) return

  const el = target.closest(`[${ID_ATTR}]`)
  if (!el) return

  const trackId = el.getAttribute(ID_ATTR)
  if (!trackId) return

  const anchor = el.closest('a')

  pushEvent('ui_click', {
    track_id: trackId,
    track_section:
      el.closest(`[${SECTION_ATTR}]`)?.getAttribute(SECTION_ATTR) ?? null,
    text: el.textContent?.trim().slice(0, MAX_TEXT_LEN) || null,
    // Whole URL on purpose — `pushEvent` runs every URL-shaped value through
    // the analytics allowlist, so a link carrying a credential is stripped
    // there rather than by each caller remembering to.
    href: anchor instanceof HTMLAnchorElement ? anchor.href : null,
  })
}
